/**
 * MarketInsightCard — 市场洞察报告卡片（含 store 集成）
 * Figma: https://www.figma.com/design/v130VaP0o6UZQCcvUbk4VG/EaseSourcing?node-id=9269-27486
 *
 * 使用方式：
 * - 在 AI 对话 subMsg 渲染器中，将 MARKET_INSIGHT_REPORT 类型的 contentValue 作为 payload 传入
 * - isLoading=true 时渲染 Skeleton（报告生成中）
 * - hasError=true 时渲染失败态（无数据/生成失败）
 * - 正常态：store 实例化后渲染完整卡片，收藏/关注/查看详情操作均通过 store 处理
 */

import { useRef, useState, useCallback } from 'react';
import cn from 'classnames';
import { Button, Tag } from '@leadsnavi/ui-kit';
import { AlertCircle, Image } from 'lucide-react';
import ArrowRightIcon from '@/assets/icons/arrow-right-v2.svg?react';
import { formatEmailCardTime } from '@/utils/dayjs';
import { ReportActionButtons } from '../report-action-buttons';
import { createMarketInsightStore } from './store';
import type { MarketInsightCardContainerProps } from './store-types';
import { getReportCoverImage } from '@/features/reports/constants';

// ─────────────────────────────────────────────
// 报告类型标签文本映射
// ─────────────────────────────────────────────
export const REPORT_TYPE_LABEL: Record<1 | 2, string> = {
  1: 'Market Trend',
  2: 'Company Audit',
};

// ─────────────────────────────────────────────
// 加载中状态（Loading）
// Figma: https://www.figma.com/design/v130VaP0o6UZQCcvUbk4VG/EaseSourcing?node-id=9464-24593
// ─────────────────────────────────────────────
function MarketInsightCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('max-w-[474px] rounded-[8px] bg-white border border-[#E5E5E5] p-5 flex flex-col gap-4', className)}
    >
      {/* ── 封面图占位区域 ── */}
      <div className="h-[141px] rounded-[8px] bg-[rgba(206,206,206,0.2)] flex items-center justify-center">
        <Image size={28} className="text-[#A3A3A3]" />
      </div>

      {/* ── 内容骨架条 ── */}
      <div className="flex flex-col gap-[7px]">
        <div
          className="h-[13px] rounded-[2px] w-full"
          style={{ background: 'linear-gradient(120deg, #E5E5E5 7%, #F3F3F3 55%, #EDEDED 100%)' }}
        />
        <div
          className="h-[13px] rounded-[2px] w-[90%]"
          style={{ background: 'linear-gradient(120deg, #E5E5E5 7%, #F3F3F3 55%, #EDEDED 100%)' }}
        />
      </div>

      {/* ── 标签占位区 ── */}
      <div className="w-full rounded-[24px] px-2 py-3 bg-[rgba(229,229,229,0.6)]" />

      {/* ── 底部操作栏 ── */}
      <div className="flex items-center justify-between">
        {/* 左侧生成中文字（渐变色） */}
        <span
          className="text-[14px] font-normal leading-[1.5] tracking-[0.07px] bg-clip-text text-transparent"
          style={{ backgroundImage: 'linear-gradient(102deg, #525252 0%, #AAAAAA 52%, #6C6C6C 91%)' }}
        >
          Report being generated...
        </span>

        {/* 右侧按钮占位 */}
        <div className="flex items-center gap-2">
          <div className="w-[92px] h-[34px] rounded-[8px] bg-[rgba(229,229,229,0.52)]" />
          <div className="w-[97px] h-[34px] rounded-[8px] bg-[rgba(229,229,229,0.52)]" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 失败态（报告生成失败）
// Figma: https://www.figma.com/design/v130VaP0o6UZQCcvUbk4VG/EaseSourcing?node-id=10339-25615
// ─────────────────────────────────────────────
function MarketInsightCardError({ className }: { className?: string }) {
  return (
    <div className={cn('w-[474px] rounded-[8px] bg-white border border-[#E5E5E5] p-5', className)}>
      <div className="rounded-[8px] bg-[#FEF2F2] h-[47px] flex items-center px-4 gap-1">
        <AlertCircle size={20} className="shrink-0 text-[#DC2626]" />
        <span className="text-[16px] font-normal leading-[1.5] text-[#DC2626]">Report generation failed.</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 正常态卡片（接入 store）
// ─────────────────────────────────────────────
function MarketInsightCardContent({
  useStore,
  onDetailOpen,
  className,
}: {
  useStore: ReturnType<typeof createMarketInsightStore>;
  onDetailOpen?: (reportId: number) => void;
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);

  // 从 store 读取状态
  const subMsgPayload = useStore(s => s.subMsgPayload);
  const detail = useStore(s => s.detail);
  const coverImage = useStore(s => s.coverImage);
  const saveLoading = useStore(s => s.saveLoading);
  const followLoading = useStore(s => s.followLoading);

  // 展示字段：detail 加载后从 detail 取，未加载前用 subMsgPayload 兜底
  const reportId = Number(detail?.reportId ?? subMsgPayload.reportId) || 0;
  const title = detail?.title ?? subMsgPayload.title;
  const summary = detail?.summary ?? '';
  const topicTitle = detail?.topic?.topicTitle ?? '';
  const createdAt = detail?.createdAt ?? '';
  const isSaved = detail?.isSaved === 1;
  const followStatus = (detail?.topic?.followStatus ?? 0) as 0 | 1 | 2;
  const reportType = (detail?.reportType ?? (subMsgPayload.type === 'COMPANY_AUDIT' ? 2 : 1)) as 1 | 2;
  const typeLabel = REPORT_TYPE_LABEL[reportType];

  // 从 store 读取 actions
  const toggleSave = useStore(s => s.toggleSave);
  const toggleFollow = useStore(s => s.toggleFollow);
  const setDetailOpen = useStore(s => s.setDetailOpen);

  const showImage = Boolean(coverImage) && !imgError;

  const handleImgError = useCallback(() => setImgError(true), []);

  const handleViewDetails = useCallback(() => {
    setDetailOpen(true);
    onDetailOpen?.(reportId);
  }, [setDetailOpen, onDetailOpen, reportId]);

  return (
    <div
      className={cn(
        '@container max-w-[474px] relative rounded-[8px] bg-white border border-[#E5E5E5] p-[14px] @[400px]:p-5 flex flex-col gap-4 overflow-hidden',
        className
      )}
    >
      {/* ── 封面图区域 ── */}
      <div className="relative h-[140px] rounded-[8px] overflow-hidden bg-[rgba(206,206,206,0.2)]">
        {showImage && (
          <img
            src={getReportCoverImage(coverImage)}
            // src={'https://fastly.picsum.photos/id/1015/800/500.jpg?hmac=pSNYsY7XYU05VD89MUu0QDrVHgAu2yZaGSC4wzh7ld8'}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover object-center"
            onError={handleImgError}
          />
        )}
        {typeLabel && (
          <span className="absolute top-0 right-0 px-2 rounded-[4px] bg-[#FFF7E8] text-[#FF7D00] text-[14px] font-normal leading-[1.5]">
            {typeLabel}
          </span>
        )}
      </div>

      {/* 报告类型角标 */}

      {/* ── 标题 + 描述 ── */}
      <div className="flex flex-col gap-1">
        <h3 className="text-[16px] font-medium leading-[1.5] text-[#09090B] line-clamp-2">{title}</h3>
        <p className="text-[14px] font-normal leading-[1.5] tracking-[0.07px] text-[#525252] line-clamp-2">{summary}</p>
      </div>

      {/* ── Topic 标签 ── */}
      {topicTitle && (
        <div className="flex flex-wrap gap-2">
          <Tag
            showTooltip
            className="bg-[rgba(229,229,229,0.6)] text-[#525252]   font-normal text-xs py-1 px-2 rounded-full border-none max-w-full  whitespace-nowrap"
          >
            {topicTitle}
          </Tag>
        </div>
      )}

      {/* ── 底部操作栏 ── */}
      <div className="flex flex-col gap-2 @[400px]:flex-row @[400px]:items-center @[400px]:justify-between">
        {/* 时间戳 */}
        <span className="text-[12px] text-[#737373] leading-[1.5] tracking-[0.18px]">
          {formatEmailCardTime(createdAt)}
        </span>

        {/* 按钮组 */}
        <div className="flex items-center gap-2">
          {/* 收藏 + 关注按钮（复用公共组件） */}
          <ReportActionButtons
            isSaved={isSaved}
            saveLoading={saveLoading}
            followStatus={followStatus}
            followLoading={followLoading}
            onToggleSave={toggleSave}
            onToggleFollow={toggleFollow}
            flowBtnClassName="px-[10px]! @[400px]:px-4px!"
          />

          {/* View Details 按钮 */}
          <Button
            onClick={handleViewDetails}
            className="border border-[rgba(4,120,87,0.1)] rounded-[8px] px-[10px] @[400px]:px-4 h-auto py-[5.5px] text-[14px] font-normal gap-1"
          >
            <span>View Details</span>
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// store 桥接层（响应式读取 detailLoading）
// ─────────────────────────────────────────────

/**
 * store 已就绪后的渲染桥接层
 * 通过 useStore hook 响应式订阅 detailLoading，
 * detailLoading=true 时展示 Skeleton，完成后切换到完整卡片内容
 */
function MarketInsightCardWithStore({
  useStore,
  onDetailOpen,
  className,
}: {
  useStore: ReturnType<typeof createMarketInsightStore>;
  onDetailOpen?: (reportId: number) => void;
  className?: string;
}) {
  const detailLoading = useStore(s => s.detailLoading);

  if (detailLoading) {
    return <MarketInsightCardSkeleton className={className} />;
  }

  return <MarketInsightCardContent useStore={useStore} onDetailOpen={onDetailOpen} className={className} />;
}

// ─────────────────────────────────────────────
// 容器组件（实例化 store）
// ─────────────────────────────────────────────

/** MarketInsightCard 容器组件扩展 Props（支持外部注入 store 实例） */
export interface MarketInsightCardProps extends MarketInsightCardContainerProps {
  /**
   * 外部注入的 store 实例（由 MarketInsightCardWithDetail 传入以实现状态共享）
   * 未传入时组件内部自行创建独立 store 实例
   */
  externalStore?: ReturnType<typeof createMarketInsightStore>;
}

/**
 * MarketInsightCard 容器组件
 *
 * 在 AI 对话消息流中，将 subMsg.contentValue 作为 payload 传入。
 * - 独立使用（Preview/测试）：内部通过 useRef 实例化 store，状态自治
 * - 配合 MarketInsightCardWithDetail 使用：通过 externalStore 注入外部 store，
 *   Card 与 Detail 共享同一 store 实例，实现状态双向同步
 */
export function MarketInsightCard({
  payload,
  isLoading,
  hasError,
  onDetailOpen,
  className,
  externalStore,
}: MarketInsightCardProps) {
  // 通过 useRef 持久化内部 store 实例（externalStore 存在时不使用）
  const internalStoreRef = useRef<ReturnType<typeof createMarketInsightStore> | null>(null);
  if (!internalStoreRef.current && !isLoading && !hasError && !externalStore) {
    internalStoreRef.current = createMarketInsightStore(payload);
  }

  // 优先使用外部注入的 store
  const resolvedStore = externalStore ?? internalStoreRef.current;

  // 加载中态：报告生成中（外部 prop）
  if (isLoading) {
    return <MarketInsightCardSkeleton className={className} />;
  }

  // 失败态
  if (hasError) {
    return <MarketInsightCardError className={className} />;
  }

  // 正常态（store 已就绪）
  if (!resolvedStore) return null;

  return <MarketInsightCardWithStore useStore={resolvedStore} onDetailOpen={onDetailOpen} className={className} />;
}

// 导出 store 创建函数供 MarketInsightDetail 使用
export { createMarketInsightStore };
export type { MarketInsightCardContainerProps } from './store-types';
